import React, { useEffect, useState, useContext, useRef } from "react";
import { useAuthFetch } from "../../hooks/useAuthFetch";
import DashboardEmptyState from "./emptyState";
import { ClockedInLocationMap } from "../../components/maps/ClockedInLocationMap";
import { DashboardUserFilter } from "./userFilter";
import { ReportingCustomerFilter } from "../reporting/filters/customerFilter";
import { ReportingSiteFilter } from "../reporting/filters/siteFilter";
import { ReportingActivityTypeFilter } from "../reporting/filters/activityTypeFilter";
import { ReportingActivityDetails } from "../reporting/details";
import { AuthContext } from "../../contexts/AuthContext";
import { BigNumber } from "../../components/visualization/bigNumber";
import { Table } from "../../components/table";
import { UserProfile } from "../../components/profile";
import { PageHeading } from "../../components/pageHeading";
import { ActiveMessages } from "../messaging/activePanel";
import {
  MapIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  PhoneIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { format, subDays } from "date-fns";
import { formatActivityDateTime } from "../../utils/dateTimeUtil";

function Dashboard() {
  const authFetch = useAuthFetch();
  const { authContext } = useContext(AuthContext);
  const orgId = authContext.organization.orgId;
  const { needsUsers, needsCustomers, needsSites, isOnboarding } =
    authContext.onboarding;

  /// Filters ///
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedActivityTypes, setSelectedActivityTypes] = useState([]);
  const [sitesOptions, setSitesOptions] = useState([]);

  const handleSelectedUsers = (id) =>
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const clearSelectedUsers = () => setSelectedUsers([]);

  const handleSelectedCustomers = (id) =>
    setSelectedCustomers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const clearSelectedCustomers = () => setSelectedCustomers([]);

  const handleSelectedSites = (id) =>
    setSelectedSites((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const clearSelectedSites = () => setSelectedSites([]);

  const handleSelectedActivityTypes = (id) =>
    setSelectedActivityTypes((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const clearSelectedActivityTypes = () => setSelectedActivityTypes([]);

  // Remove selected sites that no longer exist when customer filter changes
  useEffect(() => {
    setSelectedSites((prev) =>
      prev.filter((loc) => sitesOptions.some((opt) => opt.id === loc)),
    );
  }, [selectedCustomers, sitesOptions]);

  // Messages follow the site filter, or the customer filter through that
  // customer's sites.
  const messageSiteIds = selectedSites.length
    ? selectedSites
    : selectedCustomers.length
    ? sitesOptions.map((site) => site.id)
    : [];

  /// Fixed date range — last 24 hours ///
  const getDateRange = () => {
    const fmt = (d) => format(d, "yyyy-MM-dd HH:mm:ss");
    return { start: fmt(subDays(new Date(), 1)), end: fmt(new Date()) };
  };

  /// Map ///
  const [mapData, setMapData] = useState([]);
  const [isMapDataLoading, setIsMapDataLoading] = useState(true);
  const [clockedInUsersCache, setClockedInUsersCache] = useState([]);
  const [usersOptions, setUsersOptions] = useState([]);
  const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);

  useEffect(() => {
    if (orgId) fetchUsers();
  }, [orgId]);

  useEffect(() => {
    fetchSites();
  }, [selectedUsers]);

  const fetchUsers = async () => {
    if (!orgId) return;
    const url = new URL(
      `${process.env.REACT_APP_API_URL}/v1/users/shifts`,
      window.location.origin,
    );
    url.searchParams.append("org_id", orgId);
    const fetchPromise = authFetch(url.toString())
      .then((r) => r.json())
      .then((data) => {
        const users = data.map((u) => ({
          ...u,
          name: `${u.first_name} ${u.last_name}`,
        }));
        setUsersOptions(users);
        setSelectedUsers(users.filter((u) => u.is_active).map((u) => u.id));
      })
      .catch((err) => console.error("Error fetching users:", err));
    const timer = new Promise((r) => setTimeout(r, 500));
    Promise.all([fetchPromise, timer]).then(() =>
      setInitialLoadCompleted(true),
    );
  };

  const fetchSites = async () => {
    setIsMapDataLoading(true);
    if (selectedUsers.length === 0) {
      setMapData([]);
      setIsMapDataLoading(false);
      return;
    }
    const clockedIn = usersOptions.filter(
      (u) => selectedUsers.includes(u.id) && u.is_active,
    );
    if (clockedIn.length === 0) {
      setMapData([]);
      setIsMapDataLoading(false);
      return;
    }
    if (clockedInUsersCache.length > 0) {
      setMapData(
        clockedInUsersCache.filter((u) => selectedUsers.includes(u.id)),
      );
      setIsMapDataLoading(false);
      return;
    }
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_URL}/v1/users/clocked-in-locations?org_id=${orgId}`,
      );
      const data = await res.json();
      setClockedInUsersCache(data.users);
      setMapData(data.users.filter((u) => selectedUsers.includes(u.id)));
    } catch (err) {
      console.error("Error fetching clocked-in locations:", err);
      setMapData([]);
    }
    setIsMapDataLoading(false);
  };

  /// KPI counts — single call covering all filters ///
  const [countsLoading, setCountsLoading] = useState(true);
  const [counts, setCounts] = useState({
    incident_count: 0,
    emergency_contact_count: 0,
    checkpoint_count: 0,
  });
  const countsLoadedOnce = useRef(false);

  useEffect(() => {
    if (!orgId || !initialLoadCompleted) return;
    if (!countsLoadedOnce.current) setCountsLoading(true);
    const { start, end } = getDateRange();
    const url = new URL(
      `${process.env.REACT_APP_API_URL}/v1/activities/counts`,
      window.location.origin,
    );
    url.searchParams.append("org_id", orgId);
    url.searchParams.append("start_date", start);
    url.searchParams.append("end_date", end);
    if (selectedUsers.length)
      url.searchParams.append("user_ids", selectedUsers.join(","));
    if (selectedCustomers.length)
      url.searchParams.append("customer_ids", selectedCustomers.join(","));
    if (selectedSites.length)
      url.searchParams.append("site_ids", selectedSites.join(","));
    if (selectedActivityTypes.length)
      url.searchParams.append(
        "activity_type_ids",
        selectedActivityTypes.join(","),
      );

    authFetch(url.toString())
      .then((r) => r.json())
      .then((data) => {
        setCounts(data);
        countsLoadedOnce.current = true;
        setCountsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching counts:", err);
        setCountsLoading(false);
      });
  }, [
    orgId,
    initialLoadCompleted,
    selectedUsers,
    selectedCustomers,
    selectedSites,
    selectedActivityTypes,
  ]);

  /// Activities table ///
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const activitiesLoadedOnce = useRef(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedActivityId, setSelectedActivityId] = useState(null);

  const handleDetailsClick = (id) => {
    setSelectedActivityId(id);
    setIsDetailsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!orgId || !initialLoadCompleted) return;
    if (!activitiesLoadedOnce.current) setActivitiesLoading(true);
    const { start, end } = getDateRange();
    let url = `${process.env.REACT_APP_API_URL}/v1/reporting/activities?org_id=${orgId}`;
    url += `&start_date=${start}&end_date=${end}`;
    if (selectedCustomers.length)
      url += `&customer_ids=${selectedCustomers.join(",")}`;
    if (selectedSites.length)
      url += `&site_ids=${selectedSites.join(",")}`;
    if (selectedActivityTypes.length)
      url += `&activity_type_ids=${selectedActivityTypes.join(",")}`;
    if (selectedUsers.length) url += `&user_ids=${selectedUsers.join(",")}`;

    const fetchPromise = authFetch(url)
      .then((r) => r.json())
      .then((data) => {
        setActivities(data);
        activitiesLoadedOnce.current = true;
      })
      .catch((err) => console.error("Error fetching activities:", err));
    const timer = new Promise((r) => setTimeout(r, 500));
    Promise.all([fetchPromise, timer]).then(() => setActivitiesLoading(false));
  }, [
    orgId,
    initialLoadCompleted,
    selectedUsers,
    selectedCustomers,
    selectedSites,
    selectedActivityTypes,
  ]);

  const columns = React.useMemo(
    () => [
      {
        Header: "Customer",
        accessor: "customer_name",
        Cell: ({ row }) => (
          <a
            onClick={() => handleDetailsClick(row.original.id)}
            className="text-gray-500 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-200 hover:underline capitalize cursor-pointer"
          >
            {row.original.customer_name}
          </a>
        ),
      },
      { Header: "Site", accessor: "site_name" },
      { Header: "Activity type", accessor: "activity_type" },
      { Header: "Activity", accessor: "activity" },
      {
        Header: "Guard",
        accessor: (row) => `${row.first_name} ${row.last_name}`,
        Cell: ({ row }) => (
          <div className="flex items-center">
            <UserProfile
              imageUrl={row.original.profile_url || ""}
              size="small"
              firstName={row.original.first_name || ""}
              lastName={row.original.last_name || ""}
              useInitials={false}
            />
            <span>
              {row.original.first_name} {row.original.last_name}
            </span>
          </div>
        ),
      },
      {
        Header: "Time",
        accessor: "activity_date",
        Cell: ({ value }) => formatActivityDateTime(value),
      },
    ],
    [],
  );

  return (
    <div>
      <main>
        {isOnboarding ? (
          <DashboardEmptyState
            needsUsers={needsUsers}
            needsCustomers={needsCustomers}
            needsSites={needsSites}
          />
        ) : (
          <>
            <div className="px-4 sm:px-6 lg:px-6 pt-6">
              <PageHeading title="Dashboard" />
            </div>
            {/* Filter bar */}
            <div className="px-4 sm:px-6 lg:px-6 pb-3 ml-1">
              <div className="flex flex-wrap items-center gap-3">
                <ReportingCustomerFilter
                  selected={selectedCustomers}
                  onUpdate={handleSelectedCustomers}
                  onClear={clearSelectedCustomers}
                />
                <ReportingSiteFilter
                  selected={selectedSites}
                  onUpdate={handleSelectedSites}
                  onClear={clearSelectedSites}
                  customerIds={selectedCustomers}
                  setSitesOptions={setSitesOptions}
                />
                <ReportingActivityTypeFilter
                  selected={selectedActivityTypes}
                  onUpdate={handleSelectedActivityTypes}
                  onClear={clearSelectedActivityTypes}
                />
                <DashboardUserFilter
                  selected={selectedUsers}
                  onUpdate={handleSelectedUsers}
                  onClear={clearSelectedUsers}
                />
              </div>
            </div>

            {/* Widgets: one stack with the same gap (16px) between each, matching
                the gap between the three totals. */}
            <div className="px-4 sm:px-6 lg:px-6 pt-1 pb-6 space-y-4">
              {/* Map */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden">
                {isMapDataLoading ? (
                  <div className="flex items-center justify-center h-96">
                    <p className="text-lg text-gray-500 dark:text-gray-400">
                      Loading...
                    </p>
                  </div>
                ) : mapData && mapData.length > 0 ? (
                  <ClockedInLocationMap clockedInLocationData={mapData} />
                ) : (
                  <div className="flex flex-col items-center justify-center h-96">
                    <MapIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
                    <p className="mt-3 text-sm text-gray-400 dark:text-gray-500 text-center">
                      {selectedUsers.length === 0
                        ? "No guards selected to show on the map"
                        : "No clocked-in guards selected"}
                    </p>
                  </div>
                )}
              </div>

              {/* Messages from the last 24 hours (follows the filters) */}
              <ActiveMessages siteIds={messageSiteIds} userIds={selectedUsers} />

              {/* KPIs */}
              <div className="flex w-full">
                <BigNumber
                  isLoading={countsLoading}
                  count={counts.incident_count}
                  title="Incidents submitted"
                  icon={ExclamationTriangleIcon}
                  styles="w-1/3 mr-4"
                />
                <BigNumber
                  isLoading={countsLoading}
                  count={counts.emergency_contact_count}
                  title="Emergency calls"
                  icon={PhoneIcon}
                  styles="w-1/3 mr-4"
                />
                <BigNumber
                  isLoading={countsLoading}
                  count={counts.checkpoint_count}
                  title="Checkpoints completed"
                  icon={ShieldCheckIcon}
                  styles="w-1/3"
                />
              </div>

              {/* Activities table */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden pb-2">
                <div className="pl-3 pt-3">
                  <h3 className="text-sm tracking-wide text-gray-500 dark:text-gray-400 pb-2">
                    Activities
                  </h3>
                </div>
                <Table
                  data={activities}
                  columns={columns}
                  defaultSort={{ id: "activity_date", desc: true }}
                  isLoading={activitiesLoading}
                  emptyState={
                    <div className="flex flex-col items-center justify-center h-72">
                      <DocumentTextIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
                      <p className="mt-3 text-sm text-gray-400 dark:text-gray-500 text-center">
                        No activity in the last 24 hours
                      </p>
                    </div>
                  }
                />
              </div>
            </div>
          </>
        )}
      </main>

      <ReportingActivityDetails
        isOpen={isDetailsOpen}
        toggleOpen={handleDetailsClick}
        selectedReportingActivityId={selectedActivityId}
      />
    </div>
  );
}

export { Dashboard };
